import { addEntity, addComponent, defineQuery } from 'bitecs';
import { Structure, Position, Tile, TILE_TYPES, BIOME_TYPES } from '../world.js';
import { Renderable } from '../components.js';
import { tileQuery } from './queries.js';
import { shouldSpawnEntityType, canSpawnMoreOfType, updateEntityCount } from '../entityConfigUtils.js';
import { TILE_SIZE, CURRENT_MODE, MAP_WIDTH, MAP_HEIGHT } from '../config.js';
import { createHive } from '../entities/hive.js';
import { createBee } from '../entities/bee.js';
import { getRandomPositionNear } from '../utils/positionUtils.js';

// Constants for structure generation
const STRUCTURE_DENSITY = 0.005; // 0.5% chance per valid tile

// Structure type definitions
export const STRUCTURE_TYPES = {
    HIVE: {
        id: 1,
        name: 'Hive',
        assetName: 'hive',
        biomes: [BIOME_TYPES.FOREST, BIOME_TYPES.PLAINS], // Use numeric biome types
        maxPerBiome: 2,
        minDistance: 10 * TILE_SIZE,
    },
    NEST: {
        id: 2,
        name: 'Nest',
        assetName: 'nest',
        biomes: [BIOME_TYPES.FOREST, BIOME_TYPES.PLAINS],
        maxPerBiome: 3,
        minDistance: 8 * TILE_SIZE,
    },
    DEN: {
        id: 3,
        name: 'Den',
        assetName: 'den',
        biomes: [BIOME_TYPES.FOREST, BIOME_TYPES.MOUNTAINS],
        maxPerBiome: 2,
        minDistance: 12 * TILE_SIZE,
    }
};

// Helper function to spawn initial bees for a hive
function spawnInitialBeesForHive(world, hive, count = 3) {
    const hiveX = Position.x[hive];
    const hiveY = Position.y[hive];
    
    console.log(`[STRUCTURES] Spawning ${count} initial bees for hive #${hive} at (${hiveX}, ${hiveY})`);
    
    const createdBees = [];
    for (let i = 0; i < count; i++) {
        // Distribute bees around the hive in a circle
        const angle = (i / count) * Math.PI * 2;
        const distance = 32; // 32 units away from hive
        const beeX = hiveX + Math.cos(angle) * distance;
        const beeY = hiveY + Math.sin(angle) * distance;
        
        const bee = createBee(world, beeX, beeY, hive);
        if (bee) {
            createdBees.push(bee);
            console.log(`[STRUCTURES] Spawned bee #${bee} at (${beeX.toFixed(1)}, ${beeY.toFixed(1)})`);
        }
    }
    
    console.log(`[STRUCTURES] Created ${createdBees.length} bees for hive #${hive}`);
    return createdBees;
}

/**
 * Generate structures based on biome and terrain
 */
export function structureGenerationSystem(world) {
    // Exit early if hives (or all structures potentially) are disabled
    if (!CURRENT_MODE.structures || !CURRENT_MODE.hives) { // Check general structures flag OR specific hive flag
        console.log("[StructureSys] Structure generation skipped (disabled in CURRENT_MODE).");
        return world;
    }

    console.log('Starting structure generation...');
    
    // Get all valid land tiles
    const tiles = tileQuery(world);
    const landTiles = tiles.filter(tile => Tile.type[tile] === TILE_TYPES.LAND);
    
    console.log(`Found ${landTiles.length} land tiles for potential structure spawns`);
    
    // Track structures by type and biome
    const structureCounts = {
        HIVE: { total: 0, byBiome: {} },
        NEST: { total: 0, byBiome: {} },
        DEN: { total: 0, byBiome: {} }
    };
    
    // Track all placed structures with their positions for distance calculations
    const placedStructures = [];
    
    // Temporarily increase spawn chance for debugging
    const STRUCTURE_DENSITY = 0.01; // Increased from 0.005 to 0.01 for testing
    
    // Generate structures on valid tiles
    for (const tile of landTiles) {
        const biome = Tile.biome[tile];
        const x = Tile.x[tile];
        const y = Tile.y[tile];
        
        // Random chance to spawn a structure
        if (Math.random() < STRUCTURE_DENSITY) {
            // Find valid structure types for this biome
            const validStructureTypes = Object.entries(STRUCTURE_TYPES)
                .filter(([_, type]) => type.biomes.includes(biome))
                .map(([name, type]) => ({ name, ...type }));
            
            if (validStructureTypes.length === 0) continue;
            
            // Randomly select a structure type from valid options
            const structureType = validStructureTypes[Math.floor(Math.random() * validStructureTypes.length)];
            
            // Check if this type should be spawned based on configuration
            if (!shouldSpawnEntityType(structureType.name, 'structures')) {
                console.log(`Skipping spawn of ${structureType.name} due to spawn configuration`);
                continue;
            }
            
            // Check if we've reached the maximum count for this type in this biome
            const biomeCount = structureCounts[structureType.name]?.byBiome[biome] || 0;
            
            if (biomeCount >= structureType.maxPerBiome) {
                console.log(`Maximum count reached for ${structureType.name} in biome ${biome}`);
                continue;
            }
            
            // Check if we've reached the maximum count for this type globally
            if (!canSpawnMoreOfType(structureType.name, 'structures')) {
                console.log(`Maximum count reached for ${structureType.name}, not spawning more`);
                continue;
            }
            
            // Check minimum distance requirement
            const worldX = x * TILE_SIZE + (TILE_SIZE / 2); // Center in tile
            const worldY = y * TILE_SIZE + (TILE_SIZE / 2); // Center in tile
            const tooClose = placedStructures.some(struct => {
                const dx = struct.x - worldX;
                const dy = struct.y - worldY;
                const distSq = dx*dx + dy*dy;
                return distSq < (structureType.minDistance * structureType.minDistance);
            });
            
            if (tooClose) {
                console.log(`Location (${x}, ${y}) too close to another structure`);
                continue;
            }
            
            // Spawn the structure based on its type
            let structureEntity;
            
            if (structureType.name === 'HIVE') {
                // Debug for hive creation
                console.log(`[STRUCTURES] About to create hive at world coordinates (${worldX}, ${worldY})`);
                
                // Use the specialized createHive function
                structureEntity = createHive(world, worldX, worldY);
                
                // Added debug check for hive creation
                if (structureEntity) {
                    console.log(`[STRUCTURES] Successfully created hive #${structureEntity}`);
                    
                    // Bees are now spawned directly in the createHive function
                    // No need to call spawnInitialBeesForHive here
                } else {
                    console.error(`[STRUCTURES] Failed to create hive at (${worldX}, ${worldY})`);
                }
            } else {
                // Generic structure creation for other types
                structureEntity = spawnGenericStructure(world, x, y, structureType.id);
            }
            
            if (structureEntity) {
                // Update counts
                structureCounts[structureType.name].total++;
                structureCounts[structureType.name].byBiome[biome] = biomeCount + 1;
                updateEntityCount(structureType.name, 'structures', 1, true);
                
                // Track placed structure
                placedStructures.push({ x: worldX, y: worldY, type: structureType.name });
                
                console.log(`[STRUCTURES] Spawned ${structureType.name} entity #${structureEntity} at world coordinates (${worldX}, ${worldY})`);
                console.log(`[STRUCTURES] Entity has Structure component:`, 
                    Structure.type && Structure.type[structureEntity] !== undefined);
                console.log(`[STRUCTURES] Structure type value:`, Structure.type[structureEntity]);
            }
        }
    }
    
    // Force spawn a hive in middle of the map if no hives were created
    if (structureCounts.HIVE.total === 0) {
        console.log('[STRUCTURES] No hives generated naturally. Forcing a hive in the center of the map.');
        
        // Calculate center of map
        const centerX = Math.floor(MAP_WIDTH / 2) * TILE_SIZE + (TILE_SIZE / 2);
        const centerY = Math.floor(MAP_HEIGHT / 2) * TILE_SIZE + (TILE_SIZE / 2);
        
        // Create hive
        const forceHive = createHive(world, centerX, centerY);
        if (forceHive) {
            console.log(`[STRUCTURES] Successfully forced hive #${forceHive} at center (${centerX}, ${centerY})`);
            structureCounts.HIVE.total++;
            
            // Bees are now spawned directly in the createHive function
            // No need to call spawnInitialBeesForHive here
        }
    }
    
    // Log summary of structure generation
    console.log('Structure generation complete. Summary:');
    for (const [name, counts] of Object.entries(structureCounts)) {
        console.log(`  ${name}: ${counts.total} total`);
        for (const [biome, count] of Object.entries(counts.byBiome)) {
            console.log(`    in ${biome}: ${count}`);
        }
    }
    
    return world;
}

/**
 * Spawn a generic structure (not hive, which has special handling)
 */
function spawnGenericStructure(world, x, y, typeId) {
    const eid = addEntity(world);
    
    // Add components
    addComponent(world, Structure, eid);
    addComponent(world, Position, eid);
    
    // Initialize structure properties
    Structure.type[eid] = typeId;
    Structure.health[eid] = 100;
    Structure.maxHealth[eid] = 100;
    Structure.isRepairable[eid] = 1;
    Structure.isDestructible[eid] = 1;
    
    // Convert tile coordinates to world coordinates for Position component
    const worldX = x * TILE_SIZE + (TILE_SIZE / 2); // Center in tile
    const worldY = y * TILE_SIZE + (TILE_SIZE / 2); // Center in tile
    Position.x[eid] = worldX;
    Position.y[eid] = worldY;
    
    // Add to world entities array if it exists
    if (world.entities) {
        world.entities.push(eid);
    }
    
    return eid;
} 